/**
 * FixRunDetailPage - Phase 9 + Wave 4 proposal/diff
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, FixRunStatusResponse, FixProposal } from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { StatusBadge } from '../components/StatusBadge'
import { Diff } from '../components/design-system/Diff'
import { parseUnifiedDiff } from '../lib/parseUnifiedDiff'
import { useLocale } from '../contexts/LocaleContext'

export function FixRunDetailPage() {
  const { t } = useLocale()
  const navigate = useNavigate()
  const { fixRunId } = useParams<{ fixRunId: string }>()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<FixRunStatusResponse | null>(null)
  
  const auditId = searchParams.get('audit') || undefined
  
  useEffect(() => {
    if (!fixRunId) return
    loadFixRun()
  }, [fixRunId])
  
  async function loadFixRun() {
    if (!fixRunId) return
    
    try {
      setLoading(true)
      setError(null)
      const result = await api.getFixRunStatus(fixRunId)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fixRuns.failedLoad'))
    } finally {
      setLoading(false)
    }
  }
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (error || !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg">
          <p className="font-semibold">{t('fixRuns.errorDetail')}</p>
          <p className="text-sm mt-1">{error || t('fixRuns.notFound')}</p>
          <button
            onClick={() => void loadFixRun()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  const proposals: FixProposal[] = data.proposals?.length
    ? data.proposals
    : Array.isArray(data.continuation_metadata?.proposals)
      ? (data.continuation_metadata.proposals as FixProposal[])
      : []
  const summary = typeof data.continuation_metadata?.result_summary === 'string'
    ? data.continuation_metadata.result_summary
    : null
  
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('fixRuns.detailTitle')}</h1>
            <p className="text-sm text-gray-600 mt-1 font-mono">
              {data.fix_run_id}
            </p>
            {data.proposal_only !== false && (
              <p className="text-sm text-[var(--text-secondary)] mt-2">
                {t('fixRuns.proposalOnlyBanner')}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            {auditId && (
              <button
                onClick={() => navigate(`/audit/${auditId}/fixruns`)}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
              >
                {t('fixRuns.backHistory')}
              </button>
            )}
            <button
              onClick={() => navigate(`/audit/${data.audit_run_id}`)}
              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
            >
              {t('fixAi.viewAudit')}
            </button>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.colStatus')}</p>
              <StatusBadge status={data.status} />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.colBranch')}</p>
              <p className="text-sm font-mono truncate" title={data.target_branch}>
                {data.target_branch}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.applied')}</p>
              <p className="text-sm font-semibold text-green-600">
                {data.fixes_applied || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.failedFixes')}</p>
              <p className="text-sm font-semibold text-red-600">
                {data.fixes_failed || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('fixRuns.execDetails')}</h2>
        
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700">{t('fixRuns.baseRef')}</p>
            <p className="text-sm text-gray-900 font-mono mt-1">{data.base_ref}</p>
          </div>

          {(data.final_commit || (data.commits_created && data.commits_created.length > 0)) && (
            <div>
              <p className="text-sm font-medium text-gray-700">{t('fixAi.finalCommit')}</p>
              <p className="text-sm text-gray-900 font-mono mt-1">
                {data.final_commit || data.commits_created[0]}
              </p>
            </div>
          )}

          {summary && (
            <div>
              <p className="text-sm font-medium text-gray-700">{t('fixRuns.resultSummary')}</p>
              <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{summary}</p>
            </div>
          )}

          {data.error_message && (
            <div className="border border-[var(--error)] p-3">
              <p className="text-sm font-medium text-[var(--error)]">{t('fixRuns.errorDetail')}</p>
              <p className="text-sm mt-1">{data.error_message}</p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">{t('fixRuns.colCreated')}</p>
              <p className="text-sm text-gray-600 mt-1">
                {new Date(data.created_at).toLocaleString()}
              </p>
            </div>
            {data.completed_at && (
              <div>
                <p className="text-sm font-medium text-gray-700">{t('fixRuns.colCompleted')}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(data.completed_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('fixRuns.proposalsTitle')}</h2>
        <p className="text-sm text-[var(--text-secondary)] mb-4">{t('fixRuns.proposalsLead')}</p>
        {proposals.length === 0 ? (
          <p className="text-sm text-[var(--text-secondary)]">{t('fixRuns.proposalsEmpty')}</p>
        ) : (
          <div className="space-y-6">
            {proposals.map((proposal, idx) => {
              const lines = parseUnifiedDiff(proposal.unified_diff || '')
              return (
                <div key={`${proposal.path}-${idx}`}>
                  {proposal.summary && (
                    <p className="text-sm mb-2">{proposal.summary}</p>
                  )}
                  {lines.length === 0 ? (
                    <p className="text-sm text-[var(--text-secondary)] font-mono">
                      {proposal.path}: {t('fixRuns.proposalsEmpty')}
                    </p>
                  ) : (
                    <Diff filename={proposal.path || t('fixRuns.proposalFallback')} lines={lines} />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      
      <div className="mt-6 card">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('fixRuns.nextSteps')}
        </p>
      </div>
    </div>
  )
}
